import { useEffect, useState } from 'react'

/*
 * Lightweight access probe for the LiveKit calling feature. Calls
 * /VoiceBot/Admin/LiveKit/Mine once on mount + every refreshMs. Returns
 * {loading, granted}.
 *
 * Mirrors pages/jitsi/useJitsiAccess.js's module-wide poll-dedup pattern
 * (one poller total, no matter how many components subscribe — sidebar +
 * an open call page share the same fetch) but points at the VoiceBot
 * grant endpoint (VoiceBotLiveKitAdminController) instead of the Jitsi one,
 * since LiveKit calling reuses the existing VoiceBotSettings per-admin
 * grant pattern rather than Jitsi's separate access model.
 */

let entry = null

function ensurePoller(refreshMs) {
  if (entry) {
    if (refreshMs < entry.refreshMs) {
      clearInterval(entry.intervalId)
      entry.refreshMs = refreshMs
      entry.intervalId = setInterval(entry.tick, refreshMs)
    }
    return entry
  }
  entry = {
    state: { loading: true, granted: false },
    subscribers: new Set(),
    refreshMs,
    intervalId: null,
    tick: null,
  }
  entry.tick = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_IP}/VoiceBot/Admin/LiveKit/Mine`, {
        credentials: 'include',
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      entry.state = { loading: false, granted: !!data.enabled }
    } catch {
      entry.state = { loading: false, granted: false }
    }
    entry.subscribers.forEach((cb) => cb(entry.state))
  }
  entry.tick()
  entry.intervalId = setInterval(entry.tick, refreshMs)
  return entry
}

function subscribe(refreshMs, cb) {
  const e = ensurePoller(refreshMs)
  e.subscribers.add(cb)
  cb(e.state)
  return () => {
    e.subscribers.delete(cb)
    if (e.subscribers.size === 0) {
      clearInterval(e.intervalId)
      entry = null
    }
  }
}

export default function useLiveKitAccess(refreshMs = 30000) {
  const [state, setState] = useState({ loading: true, granted: false })

  useEffect(() => {
    const unsub = subscribe(Math.max(5000, refreshMs), setState)
    return unsub
  }, [refreshMs])

  return state
}
